/**
 * Retry utility for handling transient Firebase errors
 */

interface RetryOptions {
  maxAttempts?: number;
  delay?: number;
  backoffMultiplier?: number;
  retryCondition?: (error: any) => boolean;
}

const defaultRetryCondition = (error: any): boolean => {
  // Retry on network errors, temporary failures, but not on quota exceeded
  if (error?.code === 'resource-exhausted' || error?.message?.includes('quota exceeded')) {
    return false; // Don't retry quota errors
  }
  
  const retryableCodes = [
    'unavailable',
    'deadline-exceeded',
    'internal',
    'unknown',
    'aborted'
  ];
  
  return retryableCodes.includes(error?.code) || 
         error?.message?.includes('network') ||
         error?.message?.includes('timeout');
};

/**
 * Retry a function with exponential backoff
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxAttempts = 3,
    delay = 1000,
    backoffMultiplier = 2,
    retryCondition = defaultRetryCondition
  } = options;

  let lastError: any;
  
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      // If this is the last attempt or error is not retryable, throw
      if (attempt === maxAttempts || !retryCondition(error)) {
        throw error;
      }
      
      // Calculate delay with exponential backoff
      const delayMs = delay * Math.pow(backoffMultiplier, attempt - 1);
      
      console.warn(`Attempt ${attempt} failed, retrying in ${delayMs}ms:`, error);
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
  }
  
  throw lastError;
}

/**
 * Specific retry configuration for Firebase operations
 */
export const firebaseRetryConfig: RetryOptions = {
  maxAttempts: 3,
  delay: 1000,
  backoffMultiplier: 2,
  retryCondition: defaultRetryCondition
};

/**
 * Wrapper for Firebase operations with built-in retry logic
 */
export function withFirebaseRetry<T>(fn: () => Promise<T>): Promise<T> {
  return withRetry(fn, firebaseRetryConfig);
}
